import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';

import { Grid, IconButton, TextField, Typography } from '@mui/material';
import { ReactComponent as PdfIcon } from 'assets/icons/PDF_icon.svg';
import { ReactComponent as FileIcon } from 'assets/icons/FILE_icon.svg';
import { CN_MGMT_API_URL } from 'services/constants';
import { base64ToArrayBuffer } from 'utils/arrayBufferUtil';
import { JBColors, JBIcon, JBTheme } from '@juliusbaer-gcrm/gcrm-frontend-components';
import { FileInfo, FileObjectWithDescription } from 'views/ContactNoteForm/components/DocumentUpload/DocumentDropZone';

const styles = {
  marginIcon: {
    marginLeft: JBTheme.spacing(0.5),
    marginRight: JBTheme.spacing(0.5),
  },
  smallIcon: {
    height: '15px !important',
    width: '15px !important',
    padding: '0px',
    color: JBColors.support.greenSmoke[80],
  },
  nakedTextField: {
    width: '85%',
    padding: 0,
    margin: '-3px 0 -5px 0',
    paddingLeft: '1px',
    lineHeight: '5px',
  },
  text: {
    flex: 1,
    maxWidth: '85%',
    cursors: 'text',
    '&:hover': {
      cursor: 'text',
    },
    '& .MuiTypography-root:hover': {
      cursor: 'text',
    },
  },
  root: {
    width: '100%',
    '& .MuiTypography-root:hover': {
      cursor: 'default',
    },
  },
  panel: {
    cursor: 'default',
  },
};

/**
 * One row of the a PDF or other document attachment list.
 * @param props.index the index of the row in the array of rows
 * @param props.fileObject the file object (the document file) displayed in the row
 * @param props.fileObjects the array of all file objects (the document files)
 * @param props.setFileObject set state action to modify the file objects
 * @param props.viewOnly determines whether the delete button is displayed
 */
const DocumentFileRow = (props: {
  index: number;
  fileObject: FileObjectWithDescription | FileInfo;
  fileObjects: FileObjectWithDescription[] | FileInfo[];
  setFileObjects: Dispatch<SetStateAction<any>>;
  viewOnly?: boolean;
}) => {
  const isEmailAttachment =
    Object.prototype.hasOwnProperty.call(props.fileObject, 'id') && !(props.fileObject as FileInfo).id;
  const isFileNewUpload = !Object.prototype.hasOwnProperty.call(props.fileObject, 'id');
  const handleFieldEdit = (event: ChangeEvent<any>) => {
    props.setFileObjects(
      (props.fileObjects as FileObjectWithDescription[]).map((editedFO: FileObjectWithDescription) => {
        return props.fileObjects.indexOf(editedFO as FileObjectWithDescription & FileInfo) === props.index
          ? { ...editedFO, description: event.target.value }
          : editedFO;
      }),
    );
  };
  const [isEditing, setIsEditing] = useState(false);

  const viewAttachment = () => {
    const isFileEmail = props.fileObject.description?.endsWith('.msg');
    if (isEmailAttachment) {
      const blob = new Blob([base64ToArrayBuffer((props.fileObject as FileInfo).b64Data)], {
        type: (props.fileObject as FileInfo).mimeType,
      });
      const blobURL = URL.createObjectURL(blob);
      window.open(blobURL);
    } else if (isFileNewUpload) {
      props.fileObject.file.arrayBuffer().then((arrayBuffer: ArrayBuffer) => {
        const blob = new Blob([arrayBuffer], {
          type: isFileEmail ? 'application/vnd.ms-outlook' : props.fileObject.file.type,
        });
        const blobURL = URL.createObjectURL(blob);
        if (isFileEmail) {
          const link = document.createElement('a');
          link.href = blobURL;
          link.download = props.fileObject.description as string;
          document.body.append(link);
          link.click();
          link.remove();
        } else {
          window.open(blobURL);
        }
      });
    } else {
      const url = `${CN_MGMT_API_URL}/frontend/notes/attachments/${(props.fileObject as FileInfo).id}`;
      if (props.fileObject.description?.endsWith(`.pdf`)) {
        window.open(url);
      } else {
        const link = document.createElement('a');
        link.download = props.fileObject.description as string;
        link.href = url;
        link.click();
      }
    }
  };

  return (
    <Grid container>
      <Grid item xs={1}>
        {props.fileObject.description?.toLowerCase().endsWith('.pdf') ? <PdfIcon /> : <FileIcon />}
      </Grid>
      <Grid item xs={9}>
        {isEditing ? (
          <TextField
            autoFocus={true}
            id={`edit-name-field-${props.index}`}
            sx={styles.nakedTextField}
            InputProps={{ disableUnderline: true }}
            inputProps={{ 'aria-label': 'description' }}
            value={props.fileObject.description}
            onChange={(event) => handleFieldEdit(event)}
            onKeyPress={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                setIsEditing(false);
              }
            }}
            onBlur={() => setIsEditing(false)}
            onAuxClick={() => setIsEditing(false)}
          />
        ) : (
          <Typography
            id={`file-name-${props.index}`}
            noWrap
            sx={styles.text}
            onClick={() => setIsEditing(!props.viewOnly)}
          >
            {props.fileObject.description?.length && props.fileObject.description?.length > 0 ? (
              `${props.fileObject.description}${
                isEmailAttachment && (props.fileObject as FileInfo).name
                  ? ` - ${(props.fileObject as FileInfo).name}`
                  : ''
              }`
            ) : (
              <span>&emsp;</span>
            )}
          </Typography>
        )}
      </Grid>

      <Grid item xs={2}>
        <IconButton
          sx={styles.smallIcon}
          id={`view-attachment-iconbutton-${props.index}`}
          onClick={viewAttachment}
          size="large"
        >
          <JBIcon iconName="view" size="sm" fill={JBColors.core.reflexBlue[100]} />
        </IconButton>

        {!props.viewOnly && !isEmailAttachment && (
          <IconButton
            sx={styles.smallIcon}
            id={`delete-attachment-iconbutton-${props.index}`}
            onClick={() => {
              props.setFileObjects(
                (props.fileObjects as (FileObjectWithDescription & FileInfo)[]).filter(
                  (file: FileObjectWithDescription & FileInfo) => props.fileObjects.indexOf(file) !== props.index,
                ),
              );
            }}
            size="large"
          >
            <JBIcon iconName="deleteIcon" size="sm" fill={JBColors.core.reflexBlue[100]} />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};

export default DocumentFileRow;
